import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import { setCcStartDate, type ClientTracker } from '../../../../services/admin/clientAnalyticsService';

interface CcStartDateModalProps {
  clients: ClientTracker[];
  onClose: () => void;
  onSaved: () => void;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function CcStartDateModal({ clients, onClose, onSaved }: CcStartDateModalProps) {
  // Drafts keyed by clientId; '' means "no start date".
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(clients.map((c) => [c.clientId, c.ccStartDate ?? '']))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const original = Object.fromEntries(clients.map((c) => [c.clientId, c.ccStartDate ?? '']));

  const handleSave = async () => {
    // Only persist clients whose value actually changed.
    const changed = clients.filter((c) => drafts[c.clientId] !== original[c.clientId]);

    const invalid = changed.find((c) => {
      const v = drafts[c.clientId];
      return v !== '' && !ISO_DATE.test(v);
    });
    if (invalid) {
      setError(`Invalid date for ${invalid.clientName}. Use YYYY-MM-DD.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      for (const c of changed) {
        const v = drafts[c.clientId];
        await setCcStartDate(c.clientId, v === '' ? null : v);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className={cn(primaryText, 'text-lg', semiBoldText)}>Culinary Cloud start dates</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className={cn(secondaryText, smallTextSize, 'mb-4')}>
          The start date sets the before/after split for each client. Leave blank to skip the
          comparison.
        </p>

        <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-2">
          {clients.map((c) => (
            <div key={c.clientId} className="flex items-center justify-between gap-3">
              <span className={cn(primaryText, smallTextSize, 'truncate')}>{c.clientName}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="date"
                  value={drafts[c.clientId] ?? ''}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [c.clientId]: e.target.value }))
                  }
                  className={cn(
                    smallTextSize,
                    'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-200'
                  )}
                />
                {drafts[c.clientId] && (
                  <button
                    onClick={() => setDrafts((prev) => ({ ...prev, [c.clientId]: '' }))}
                    className="text-gray-400 dark:text-textQuaternary-dark hover:text-red-500 dark:hover:text-textRed-dark"
                    title="Clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {error && <p className="text-red-600 dark:text-red-400 text-sm mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={saving}
            className={cn(
              smallTextSize,
              semiBoldText,
              'px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50'
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              smallTextSize,
              semiBoldText,
              'px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2'
            )}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
